import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, useRole } from "../hooks/useAuth";
import {
  MenuIcon,
  DocumentIcon,
  BellIcon,
  CogIcon,
  ArchiveIcon,
  SentrilogIcon,
} from "./Icons";
import { APP_NAME } from "../constants/expiry";
import { useNotifications } from "../hooks/useNotifications";
import { formatDistanceToNow, parseISO } from "date-fns";
import { usePlan } from "../hooks/usePlan";
import { LockedOverlay, PlanBadge, UpgradeModal } from "./UpgradePrompt";
import type { PlanLimits } from "../config/plans.config";

interface HeaderProps {
  onAddExpiry?: () => void;
  onImport?: () => void;
  onSettings?: () => void;
  onOpenPricing?: () => void;
  notificationCount?: number;
}

// ─── What's New feed ──────────────────────────────────────────────────────────
interface Release {
  id: number;
  tag: "New" | "Improved" | "Fix";
  title: string;
  body: string;
  date: string;
  isNew: boolean;
}

const RELEASES: Release[] = [
  {
    id: 1,
    tag: "New",
    title: "Owner chip-input",
    body: "Assign multiple owners to any document using the new chip input. Type an email or pick from your team list.",
    date: "Mar 11, 2026",
    isNew: true,
  },
  {
    id: 2,
    tag: "New",
    title: "Document history timeline",
    body: "Every create, edit, renewal, and assignment is now tracked in a beautiful timeline inside the edit modal.",
    date: "Mar 11, 2026",
    isNew: true,
  },
  {
    id: 3,
    tag: "Improved",
    title: "Mark as Renewal flow",
    body: 'Expiry date is locked by default in edit mode. Click "Mark as Renewal" to unlock, set the new date, and log it in history.',
    date: "Mar 10, 2026",
    isNew: false,
  },
  {
    id: 4,
    tag: "New",
    title: "CSV Import with Owner column",
    body: "The import template now includes an Owner Email column. Matching emails are auto-resolved to team members.",
    date: "Mar 9, 2026",
    isNew: false,
  },
  {
    id: 5,
    tag: "Improved",
    title: "Summary card filtering",
    body: "Click High Risk, Upcoming, or Needs Attention cards to instantly filter the table. Click again to clear.",
    date: "Mar 8, 2026",
    isNew: false,
  },
  {
    id: 6,
    tag: "New",
    title: "Category dropdown",
    body: 'Documents now use a predefined category list with an "Others" option for custom categories.',
    date: "Mar 7, 2026",
    isNew: false,
  },
  {
    id: 7,
    tag: "Fix",
    title: "Focus styles polished",
    body: "Removed aggressive blue outlines on click. Buttons now use a soft shadow ring; inputs use a quiet border.",
    date: "Mar 6, 2026",
    isNew: false,
  },
];

const TAG_STYLES: Record<Release["tag"], string> = {
  New: "bg-[#EFF6FF] text-[#2563EB]",
  Improved: "bg-[#DCFCE7] text-[#15803D]",
  Fix: "bg-[#FEF3C7] text-[#D97706]",
};

// Plan badge colours (keyed by plan name from plans.config.ts)
const SUB_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  free: { bg: "bg-[#F3F4F6]", text: "text-[#374151]", label: "Free" },
  pro: { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]", label: "Pro" },
  business: { bg: "bg-[#FEF3C7]", text: "text-[#D97706]", label: "Business" },
  enterprise: {
    bg: "bg-[#F5F3FF]",
    text: "text-[#7C3AED]",
    label: "Enterprise",
  },
};

const Header: React.FC<HeaderProps> = ({
  onAddExpiry,
  onImport,
  onSettings,
  onOpenPricing,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, workspaces, activeWorkspaceId, switchWorkspace } = useAuth();
  const { isAdmin, canEdit } = useRole();
  const { plan, canUse, requiredPlanFor } = usePlan();
  const [upgradeFeature, setUpgradeFeature] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [emailPanelOpen, setEmailPanelOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [workspaceSwitching, setWorkspaceSwitching] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const emailRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);

  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (workspaceId === activeWorkspaceId || workspaceSwitching) return;
    setWorkspaceSwitching(true);
    try {
      await switchWorkspace(workspaceId);
      setProfileOpen(false);
      // Reload to re-fetch all data for the new workspace
      window.location.reload();
    } catch {
      // silently ignore
    } finally {
      setWorkspaceSwitching(false);
    }
  };

  // ── Live notification data ─────────────────────────────────────────────────
  const {
    groups: notifGroups,
    unreadCount,
    markAllRead,
    isLoading: notifLoading,
  } = useNotifications();
  // "What's new" badge = hardcoded unread releases (count of isNew)
  const whatsNewCount = RELEASES.filter((r) => r.isNew).length;
  // Total badge = automated email unread + whats-new unread (reserved for future combined indicator)
  const _totalBadge = unreadCount + whatsNewCount; // eslint-disable-line @typescript-eslint/no-unused-vars

  const subStyle = SUB_STYLES[plan] ?? SUB_STYLES["free"];

  const userInitials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : user?.email
      ? user.email[0].toUpperCase()
      : "U";

  const handleBellClick = () => {
    setProfileOpen(false);
    setEmailPanelOpen(false);
    setNotifOpen((o) => !o);
  };

  const handleEmailIconClick = () => {
    setProfileOpen(false);
    setNotifOpen(false);
    if (!emailPanelOpen) {
      markAllRead();
    }
    setEmailPanelOpen((o) => !o);
  };

  const handleAddWithAI = () => {
    if (onAddExpiry) onAddExpiry();
    else navigate("/items?new=1");
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
      if (emailRef.current && !emailRef.current.contains(e.target as Node)) {
        setEmailPanelOpen(false);
      }
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen, emailPanelOpen]);

  const navLinks: Array<{
    label: string;
    path: string;
    icon: React.ReactNode;
    pro?: boolean;
  }> = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
    {
      label: "All Documents",
      path: "/items",
      icon: <DocumentIcon className="h-4 w-4" />,
    },
    {
      label: "Archive",
      path: "/archived",
      icon: <ArchiveIcon className="h-4 w-4" />,
    },
    {
      label: "Audit Trail",
      path: "/audit",
      icon: (
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
      pro: true, // shows a badge when user is on free plan
    },
  ];

  return (
    <>
      {/* ── Sidebar overlay ──────────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Slide-out sidebar ────────────────────────────────────────────────── */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full w-60 bg-white border-r border-[#E5E7EB] shadow-xl z-50 flex flex-col transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Sidebar header */}
        <button
          onClick={() => {
            navigate("/items");
            setSidebarOpen(false);
          }}
          className="flex items-center gap-2.5 px-4 h-14 border-b border-[#E5E7EB] w-full hover:opacity-80 transition-opacity"
          aria-label="Go to dashboard"
        >
          <div className="h-7 w-7 bg-[#2563EB] rounded flex items-center justify-center flex-shrink-0">
            <SentrilogIcon className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-[#111827]">
            {APP_NAME}
          </span>
        </button>

        {/* Nav links */}
        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <button
                key={link.path}
                onClick={() => {
                  navigate(link.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#EFF6FF] text-[#2563EB]"
                    : "text-[#374151] hover:bg-[#F9FAFB] hover:text-[#111827]"
                }`}
              >
                <span
                  className={isActive ? "text-[#2563EB]" : "text-[#6B7280]"}
                >
                  {link.icon}
                </span>
                <span className="flex-1 text-left">{link.label}</span>
                {link.pro && !canUse("auditTrail") && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium leading-none">
                    Pro
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="border-t border-[#E5E7EB] px-4 py-3">
          <p className="text-xs text-[#9CA3AF]">Navigate between views</p>
        </div>
      </div>

      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Left – Hamburger + Logo */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSidebarOpen((o) => !o)}
                className="p-1.5 rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"
                aria-label="Menu"
              >
                <MenuIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate("/items")}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                aria-label="Go to dashboard"
              >
                <div className="h-7 w-7 bg-[#2563EB] rounded flex items-center justify-center">
                  <SentrilogIcon className="h-4 w-4 text-white" />
                </div>
                <h1 className="text-base font-semibold text-[#111827]">
                  {APP_NAME}
                </h1>
              </button>
            </div>

            {/* Right – Actions + Icons + Avatar */}
            <div className="flex items-center space-x-2">
              {/* ── Add with AI — hidden for viewers ─────────────────── */}
              {canEdit && (
                <button
                  onClick={handleAddWithAI}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-[#2563EB] hover:bg-[#1D4ED8] transition-colors"
                >
                  <svg
                    className="h-3.5 w-3.5 flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                    />
                  </svg>
                  Add with AI
                </button>
              )}

              {/* ── Import — Pro+ only, hidden for viewers ────────── */}
              {canEdit &&
                (canUse("csvImport") ? (
                  <button
                    onClick={onImport}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-[#374151] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:border-[#D1D5DB] transition-colors"
                  >
                    Import
                  </button>
                ) : (
                  <LockedOverlay
                    feature="CSV Import"
                    requiredPlan={requiredPlanFor("csvImport")}
                    onClick={() => setUpgradeFeature("csvImport")}
                    tooltipBelow
                  >
                    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-[#374151] bg-white border border-[#E5E7EB]">
                      <svg
                        className="h-3.5 w-3.5 flex-shrink-0 text-[#6B7280]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                        />
                      </svg>
                      Import
                      <PlanBadge requiredPlan={requiredPlanFor("csvImport")} />
                    </button>
                  </LockedOverlay>
                ))}

              {canEdit && <div className="w-px h-6 bg-[#E5E7EB] mx-1" />}

              {/* ── Email icon — dedicated Emails Sent panel ────────────── */}
              <div ref={emailRef} className="relative">
                <button
                  onClick={handleEmailIconClick}
                  className="relative p-1.5 rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"
                  aria-label="Emails Sent"
                  title="Emails Sent"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-[#0891B2] text-white text-[9px] font-bold flex items-center justify-center leading-none">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>

                {emailPanelOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl border border-[#E5E7EB] shadow-xl z-50 overflow-hidden flex flex-col max-h-[520px]">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#F0FDFE]">
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-full bg-[#ECFEFF] flex items-center justify-center">
                          <svg
                            className="h-3.5 w-3.5 text-[#0891B2]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <p className="text-sm font-semibold text-[#0891B2]">
                          Emails Sent
                        </p>
                      </div>
                      <p className="text-xs text-[#9CA3AF]">Last 50</p>
                    </div>
                    {/* Content */}
                    {notifLoading ? (
                      <div className="flex-1 flex items-center justify-center py-12">
                        <div className="animate-spin h-6 w-6 rounded-full border-2 border-[#0891B2] border-t-transparent" />
                      </div>
                    ) : notifGroups.length === 0 ? (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-center px-4">
                        <div className="h-10 w-10 rounded-full bg-[#ECFEFF] flex items-center justify-center mb-3">
                          <svg
                            className="h-5 w-5 text-[#0891B2]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-[#374151]">
                          No emails sent yet
                        </p>
                        <p className="text-xs text-[#9CA3AF] mt-1">
                          Automated reminders will appear here.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-y-auto flex-1">
                        {notifGroups.map((group) => (
                          <div key={group.label}>
                            <div className="sticky top-0 bg-[#F9FAFB] px-4 py-1.5 border-b border-[#E5E7EB]">
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
                                {group.label}
                              </span>
                            </div>
                            <div className="divide-y divide-[#F3F4F6]">
                              {group.events.map((ev) => (
                                <div
                                  key={ev.id}
                                  className="px-4 py-3 flex items-start gap-3"
                                >
                                  <div className="flex-shrink-0 h-7 w-7 rounded-full bg-[#ECFEFF] flex items-center justify-center mt-0.5">
                                    <svg
                                      className="h-3.5 w-3.5 text-[#0891B2]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#111827] truncate">
                                      {ev.item_name}
                                    </p>
                                    <p className="text-xs text-[#6B7280] mt-0.5 leading-relaxed">
                                      {ev.description}
                                    </p>
                                    <p className="text-[10px] text-[#9CA3AF] mt-1">
                                      {formatDistanceToNow(parseISO(ev.date), {
                                        addSuffix: true,
                                      })}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Bell icon — What's New only ─────────────────────────── */}
              <div ref={notifRef} className="relative">
                <button
                  onClick={handleBellClick}
                  className="relative p-1.5 rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"
                  aria-label="What's New"
                >
                  <BellIcon className="h-6 w-6" />
                  {whatsNewCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center leading-none">
                      {whatsNewCount > 9 ? "9+" : whatsNewCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl border border-[#E5E7EB] shadow-xl z-50 overflow-hidden flex flex-col max-h-[520px]">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#FAFBFF]">
                      <div className="flex items-center gap-2">
                        <BellIcon className="h-4 w-4 text-[#2563EB]" />
                        <p className="text-sm font-semibold text-[#2563EB]">
                          What's New
                        </p>
                        {whatsNewCount > 0 && (
                          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-[#2563EB] text-white text-[9px] font-bold">
                            {whatsNewCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="overflow-y-auto divide-y divide-[#F3F4F6]">
                      {RELEASES.map((r) => (
                        <div
                          key={r.id}
                          className={`px-4 py-3.5 ${r.isNew ? "bg-[#FAFBFF]" : ""}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <span
                              className={`mt-1.5 flex-shrink-0 h-2 w-2 rounded-full ${r.isNew ? "bg-[#2563EB]" : "bg-transparent"}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${TAG_STYLES[r.tag]}`}
                                >
                                  {r.tag}
                                </span>
                                <p className="text-sm font-medium text-[#111827] leading-snug">
                                  {r.title}
                                </p>
                              </div>
                              <p className="text-xs text-[#6B7280] leading-relaxed">
                                {r.body}
                              </p>
                              <p className="text-[10px] text-[#9CA3AF] mt-1">
                                {r.date}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-[#F3F4F6] px-4 py-2.5 flex items-center justify-center">
                      <p className="text-xs text-[#9CA3AF]">
                        You're all caught up 🎉
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings gear */}
              <button
                onClick={onSettings}
                className="p-1.5 rounded-md text-[#6B7280] hover:bg-[#F3F4F6]"
                aria-label="Settings"
              >
                <CogIcon className="h-6 w-6" />
              </button>

              {/* Avatar + Profile dropdown */}
              <div ref={profileRef} className="relative">
                <button
                  onClick={() => setProfileOpen((o) => !o)}
                  className="flex-shrink-0 h-8 w-8 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors ring-2 ring-offset-1 ring-transparent hover:ring-[#BFDBFE]"
                  aria-label="User menu"
                >
                  {userInitials}
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-[#E5E7EB] shadow-xl z-50 overflow-hidden">
                    {/* User info */}
                    <div className="px-4 pt-4 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-base font-bold flex-shrink-0">
                          {userInitials}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#111827] truncate">
                            {user?.name || "User"}
                          </p>
                          <p className="text-xs text-[#6B7280] truncate">
                            {user?.email}
                          </p>
                          {activeWorkspace && (
                            <p className="text-[10px] text-[#9CA3AF] truncate mt-0.5 flex items-center gap-1">
                              <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              {activeWorkspace.name}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Subscription badge */}
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-[#9CA3AF]">
                          Current plan
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${subStyle.bg} ${subStyle.text}`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {subStyle.label}
                        </span>
                      </div>
                    </div>

                    {/* Workspace switcher — shown when user belongs to 2+ workspaces */}
                    {workspaces.length > 1 && (
                      <>
                        <div className="border-t border-[#F3F4F6]" />
                        <div className="px-4 py-2">
                          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                            Workspaces
                          </p>
                          <div className="space-y-0.5">
                            {workspaces.map((ws) => (
                              <button
                                key={ws.id}
                                onClick={() => handleSwitchWorkspace(ws.id)}
                                disabled={workspaceSwitching}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                                  ws.id === activeWorkspaceId
                                    ? "bg-[#EFF6FF] text-[#2563EB]"
                                    : "text-[#374151] hover:bg-[#F9FAFB]"
                                }`}
                              >
                                <div
                                  className={`h-6 w-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                    ws.id === activeWorkspaceId
                                      ? "bg-[#2563EB] text-white"
                                      : "bg-[#E5E7EB] text-[#6B7280]"
                                  }`}
                                >
                                  {ws.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="flex-1 text-left truncate text-xs font-medium">
                                  {ws.name}
                                </span>
                                {ws.is_personal && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-[#F3F4F6] text-[#9CA3AF]">
                                    Personal
                                  </span>
                                )}
                                {ws.id === activeWorkspaceId && (
                                  <svg className="h-3.5 w-3.5 text-[#2563EB] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <div className="border-t border-[#F3F4F6]" />

                    {/* Menu items */}
                    <div className="py-1.5">
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          onOpenPricing?.();
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <svg
                          className="h-4 w-4 text-[#6B7280]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                        Payment &amp; Plans
                        <svg
                          className="h-3.5 w-3.5 text-[#9CA3AF] ml-auto"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>

                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          onSettings?.();
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <svg
                          className="h-4 w-4 text-[#6B7280]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Settings
                      </button>

                      {/* Team management */}
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate("/team");
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <svg
                          className="h-4 w-4 text-[#6B7280]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Team
                        {isAdmin && (
                          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                            Admin
                          </span>
                        )}
                      </button>

                      {/* Audit Trail */}
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          navigate("/audit");
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#374151] hover:bg-[#F9FAFB] transition-colors"
                      >
                        <svg
                          className="h-4 w-4 text-[#6B7280]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                          />
                        </svg>
                        Audit Trail
                        {!canUse("auditTrail") && (
                          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                            Pro
                          </span>
                        )}
                      </button>
                    </div>

                    <div className="border-t border-[#F3F4F6]" />

                    <div className="py-1.5">
                      <button
                        onClick={() => {
                          setProfileOpen(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Upgrade modal ──────────────────────────────────────────────── */}
      {upgradeFeature && (
        <UpgradeModal
          feature={
            upgradeFeature === "csvImport" ? "CSV Import" : upgradeFeature
          }
          requiredPlan={requiredPlanFor(upgradeFeature as keyof PlanLimits)}
          onClose={() => setUpgradeFeature(null)}
        />
      )}
    </>
  );
};

export default Header;
